/* eslint react/jsx-max-depth: ["error", {max: 5}] -- applies to entire file */
import { useState } from "react";

import type { ImageSourcePropType } from "react-native";

import {
  AnalyticsComponent,
  AnalyticsFieldName,
  ExploreResourcesAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import {
  ExploreResourcesOverviewCardSize,
  ResourceType,
} from "@/constants/Explore";
import { ModalPath, TabPath } from "@/constants/Navigation";
import { TextTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import useShare from "@/hooks/useShare";
import { useAnalytics } from "@/services/Analytics";
import useAppMonitor from "@/services/AppMonitor";
import PdfViewerModal from "@/ui/composed/media/pdfViewer/PdfViewerModal";
import { Image, Text } from "@/ui/core";
import { BaseCard, Box, VStack } from "@/ui/layout";
import VideoSVG from "@/ui/theme/icons/illustrations/VideoSVG";
import { encodeNavigationParams, openUrl } from "@/utils/navigation.util";
import { isResourceShareable } from "@/utils/resource.util";
import { joinTestIDs } from "@/utils/string.util";

interface ExploreResourcesOverviewCardProps {
  carouselIndex?: number;
  carouselTitle?: string;
  imageUrl: ImageSourcePropType;
  link: string;
  testID: string;
  title: string;
  type: ResourceType;
}

const ExploreResourcesOverviewCard = ({
  carouselIndex,
  carouselTitle,
  imageUrl,
  link: url,
  testID,
  title,
  type,
}: ExploreResourcesOverviewCardProps) => {
  const { track } = useAnalytics();
  const { captureException } = useAppMonitor();
  const [isPdfOpen, setIsPdfOpen] = useState(false);
  const { shareFile } = useShare();

  const handlePress = async () => {
    track(ExploreResourcesAnalyticsEvent.ResourcesOverviewCardPressed, {
      [AnalyticsFieldName.Component]: AnalyticsComponent.Card,
      [AnalyticsFieldName.Source]: TabPath.Explore,
      [AnalyticsFieldName.Title]: title,
    });
    if (type === ResourceType.Article) {
      void openUrl({
        onError: (error?: Error) => {
          captureException(
            new Error(`Failed to open article from Explore screen: ${title}`, {
              cause: error,
            }),
          );
        },
        url,
      });
    } else if (type === ResourceType.File) {
      setIsPdfOpen(true);
    } else {
      router.navigate({
        params: encodeNavigationParams({
          carouselIndex,
          carouselTitle,
          componentSource: ExploreResourcesOverviewCard.name,
          title,
          url,
        }),
        pathname: ModalPath.VideoContent,
      });
    }
  };

  return (
    <>
      <BaseCard
        maxHeight={ExploreResourcesOverviewCardSize.Height}
        maxWidth={ExploreResourcesOverviewCardSize.Width}
        onPress={handlePress}
        testID={testID}
        variant="fullBleed"
      >
        <Box position="relative">
          <VStack alignItems="stretch" gap={12}>
            <Box position="relative">
              <Image
                contentFit="cover"
                size={{ height: 112, width: "100%" }}
                testID={testID}
                uriSource={imageUrl}
              />
              {type === ResourceType.Video && (
                <Box left={18} position="absolute" top={18}>
                  <VideoSVG testID={joinTestIDs(testID, "video")} />
                </Box>
              )}
            </Box>
            <Box paddingBottom={18} paddingHorizontal={18}>
              <Text
                numberOfLines={3}
                testID={joinTestIDs(testID, TextTestID.Title)}
                variant="bodyMedium"
              >
                {title}
              </Text>
            </Box>
          </VStack>
        </Box>
      </BaseCard>

      {type === ResourceType.File && (
        <PdfViewerModal
          isOpen={isPdfOpen}
          isShareable={isResourceShareable(title)}
          onClose={() => setIsPdfOpen(false)}
          onShare={() =>
            void shareFile({
              fileUrl: url,
              title,
            })
          }
          title={title}
          uri={url}
        />
      )}
    </>
  );
};

export default ExploreResourcesOverviewCard;
export type { ExploreResourcesOverviewCardProps };
